// src/services/api.js
import axios from 'axios';

export const API_BASE_URL = '/backend/api';
export const BACKEND_BASE = '/backend';


// Interceptor para añadir el token a todas las peticiones
// Fase H: endpoints que aceptan `idioma` (prompts/IA/voz). El interceptor lo añade
// automáticamente al body para no tener que tocar cada llamador.
const IDIOMA_ENDPOINTS = [
    '/consulta.php',
    '/realtime-session.php',
    '/whisper.php',
    '/save-realtime-transcripts.php',
    '/mentor/start.php',
    '/mentor/chat.php',
    '/mentor/advance.php',
];

axios.interceptors.request.use(
    config => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        // Inyectar idioma activo si aplica (header siempre, body/query según método)
        const url = config.url || '';
        if (IDIOMA_ENDPOINTS.some(ep => url.includes(ep))) {
            const idioma = localStorage.getItem('idioma_preferido') || 'es';
            config.headers = config.headers || {};
            config.headers['X-Idioma'] = idioma;
            if (config.data && typeof config.data === 'object' && !Array.isArray(config.data) && !(config.data instanceof FormData)) {
                if (!config.data.idioma) config.data = { ...config.data, idioma };
            } else {
                // GET o sin body: como query param para que llegue al backend
                config.params = { ...(config.params || {}), idioma };
            }
        }
        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

// 🆕 Interceptor para manejar respuestas y errores 401
axios.interceptors.response.use(
    response => {
        // Alertas que trae la respuesta (guardar avance, evaluación): la campana y el
        // aviso en pantalla (AvisoAlertas) se enteran al instante, sin otra consulta.
        const alertas = response?.data?._alertas;
        if (alertas) {
            try { window.dispatchEvent(new CustomEvent('mentoria:alertas', { detail: alertas })); } catch (e) { /* navegador antiguo */ }
        }
        return response;
    },
    error => {
        if (error.response && error.response.status === 401) {
            const requestUrl = error.config?.url || '';

            // No tratar como "sesión expirada" las rutas de autenticación
            // (login/register devuelven 401 por credenciales inválidas, no por sesión)
            const isAuthRoute = requestUrl.includes('/auth.php') ||
                                requestUrl.includes('/login') ||
                                requestUrl.includes('/register');

            // Solo disparar "sesión expirada" si había un token activo (sesión real)
            const hadToken = localStorage.getItem('token');

            if (!isAuthRoute && hadToken) {
                console.log('🚨 Error 401: Token expirado o inválido');

                localStorage.removeItem('token');
                localStorage.removeItem('user');

                // Proyectos con el acceso en la portada (INS): sin aviso, directo a esa dirección.
                const destinoExpirada = window.__sesionExpiradaDestino;
                if (destinoExpirada) {
                    window.location.href = destinoExpirada;
                    return Promise.reject(error);
                }

                window.dispatchEvent(new CustomEvent('sessionExpired', {
                    detail: { message: 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.' }
                }));

                setTimeout(() => {
                    window.location.href = '/login';
                }, 1500);
            }
        }

        return Promise.reject(error);
    }
);

export const certificadoService = {
  // PDF real con datos de ejemplo, para revisar el diseño antes de emitir.
  pdfPrueba: (proyectoId, certificado) => axios.post(`${API_BASE_URL}/admin/certificado-prueba.php`, { proyecto_id: proyectoId, certificado }, { responseType: 'blob' }),
    getCertificadosByUser: (userId) =>
      axios.get(`${API_BASE_URL}/certificados.php?user_id=${userId}`),
    misCertificados: () =>
      axios.get(`${API_BASE_URL}/certificados-v2.php?action=mis_certificados`),
    miProgreso: (proyectoId) =>
      axios.get(`${API_BASE_URL}/certificados-v2.php?action=mi_progreso${proyectoId ? `&proyecto_id=${proyectoId}` : ''}`),
    descargarPDF: (id) =>
      axios.get(`${API_BASE_URL}/certificados-v2.php?action=descargar&id=${id}`, { responseType: 'blob' }),
    verificar: (codigo) =>
      axios.get(`${API_BASE_URL}/certificados-v2.php?action=verificar&codigo=${codigo}`),
    verificarEmision: (proyectoId) =>
      axios.post(`${API_BASE_URL}/certificados-v2.php?action=verificar_emision`, { proyecto_id: proyectoId }),
    uploadBackground: (proyectoId, file) => {
      const formData = new FormData();
      formData.append('proyecto_id', proyectoId);
      formData.append('imagen', file);
      return axios.post(`${API_BASE_URL}/admin/certificado-upload.php`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    porProyecto: (proyectoId) =>
      axios.get(`${API_BASE_URL}/certificados-v2.php?action=por_proyecto&proyecto_id=${proyectoId}`),
    descargarAdmin: (id) =>
      axios.get(`${API_BASE_URL}/certificados-v2.php?action=descargar_admin&id=${id}`, { responseType: 'blob' }),
    descargarZip: (proyectoId) =>
      axios.get(`${API_BASE_URL}/certificados-v2.php?action=descargar_zip&proyecto_id=${proyectoId}`, { responseType: 'blob' }),
    emitirPendientes: (proyectoId) =>
      axios.post(`${API_BASE_URL}/certificados-v2.php?action=emitir_pendientes`, { proyecto_id: proyectoId })
  };

// Encuesta de satisfacción (NPS) por certificado
// Acceso SSO hacia otra plataforma (el protocolo del plugin «SSO Gateway Client»).
// Datos Educare: CSV de avance de la plataforma externa Educare (ficha → Datos Educare).
export const educareService = {
  obtener: (proyectoId, params = {}) =>
    axios.get(`${API_BASE_URL}/admin/educare-datos.php`, { params: { proyecto_id: proyectoId, ...params } }),
  // cambios: { habilitado?, crea_cuentas? }
  ajustes: (proyectoId, cambios) =>
    axios.post(`${API_BASE_URL}/admin/educare-datos.php`, { proyecto_id: proyectoId, accion: 'habilitar', ...cambios }),
  importar: (proyectoId, archivo, reemplazar) => {
    const fd = new FormData();
    fd.append('proyecto_id', proyectoId);
    fd.append('archivo', archivo);
    fd.append('reemplazar', reemplazar ? '1' : '0');
    return axios.post(`${API_BASE_URL}/admin/educare-datos.php`, fd);
  },
  vaciar: (proyectoId) =>
    axios.post(`${API_BASE_URL}/admin/educare-datos.php`, { proyecto_id: proyectoId, accion: 'vaciar' }),
  exportarCsv: (proyectoId, params = {}) =>
    axios.get(`${API_BASE_URL}/admin/educare-datos.php`, { params: { proyecto_id: proyectoId, formato: 'csv', ...params }, responseType: 'blob' }),
};

// Captcha propio (cuando el proyecto no usa Cloudflare Turnstile).
export const captchaService = {
  obtener: (tipo = 'op') => axios.get(`${API_BASE_URL}/captcha.php`, { params: { tipo } }),
};

export const ssoGatewayService = {
  // Público: manda el correo y devuelve la dirección de entrada de la otra web.
  solicitar: (data) => axios.post(`${API_BASE_URL}/sso-gateway.php`, data),
  // Admin
  obtener: (proyectoId) => axios.get(`${API_BASE_URL}/admin/sso-gateway.php?proyecto_id=${proyectoId}`),
  guardar: (data) => axios.put(`${API_BASE_URL}/admin/sso-gateway.php`, data),
  probar: (proyectoId, email) => axios.post(`${API_BASE_URL}/admin/sso-gateway.php`, { proyecto_id: proyectoId, accion: 'probar', email }),
};

export const encuestaService = {
    estado: (proyectoId, certificadoKey) =>
      axios.get(`${API_BASE_URL}/encuesta.php?action=estado&proyecto_id=${proyectoId}&certificado_key=${encodeURIComponent(certificadoKey || '')}`),
    responder: (proyectoId, certificadoKey, respuestas) =>
      axios.post(`${API_BASE_URL}/encuesta.php?action=responder`, { proyecto_id: proyectoId, certificado_key: certificadoKey, respuestas }),
    resumen: (proyectoId, certificadoKey) =>
      axios.get(`${API_BASE_URL}/encuesta.php?action=resumen&proyecto_id=${proyectoId}&certificado_key=${encodeURIComponent(certificadoKey || '')}`),
    exportar: (proyectoId, certificadoKey) =>
      axios.get(`${API_BASE_URL}/encuesta.php?action=exportar&proyecto_id=${proyectoId}&certificado_key=${encodeURIComponent(certificadoKey || '')}`, { responseType: 'blob' }),
  };
  

  export const adminService = {
    getDashboardStats: () => {
      return axios.get(`${API_BASE_URL}/admin/dashboard-stats.php`);
      },

      // Gestión de servicio de voz
    getVoiceService: () => {
      return axios.get(`${API_BASE_URL}/admin/voice-service.php`);
  },
  
  updateVoiceService: (config) => {
      return axios.post(`${API_BASE_URL}/admin/voice-service.php`, config);
  },
  
  getAvailableVoices: (service) => {
      return axios.get(`${API_BASE_URL}/voices.php?service=${service}`);
  },

  // Correcciones STT (Speech-to-Text)
  getSttCorrections: () => {
      return axios.get(`${API_BASE_URL}/admin/stt-corrections.php`);
  },
  updateSttCorrections: (corrections) => {
      return axios.post(`${API_BASE_URL}/admin/stt-corrections.php`, { corrections });
  }

  };
  

// Servicios de progreso del usuario
export const progressService = {
    getUserProgress: (userId) => {
        return axios.get(`${API_BASE_URL}/progress.php?user_id=${userId}`);
    },
    saveProgress: (progressData) => {
        return axios.post(`${API_BASE_URL}/progress.php`, progressData);
    },
    getLastActivity: (userId) => {
        return axios.get(`${API_BASE_URL}/progress.php?user_id=${userId}&last_activity=true`);
    },

    getFullCourseProgress: (userId, cursoId, moduleId) => {
        return axios.get(`${API_BASE_URL}/progress.php?user_id=${userId}&curso_id=${cursoId}&module_id=${moduleId}`);
      },

      registerCompletedCourse: ({ user_id, curso_id }) => {
        return axios.post(`${API_BASE_URL}/completed_course.php`, {
            user_id,
            curso_id
        });   
    },

      // Nuevos servicios para administradores
  getSystemProgress: () => {
    return axios.get(`${API_BASE_URL}/admin/progress-stats.php`);
  },
  
  getActivityByDay: () => {
    return axios.get(`${API_BASE_URL}/admin/activity-by-day.php`);
  },
  
  getUserRegistrationsByDate: () => {
    return axios.get(`${API_BASE_URL}/admin/user-registrations.php`);
  },
  
  getCourseCompletion: (courseId) => {
    return axios.get(`${API_BASE_URL}/admin/course-completion.php?course_id=${courseId}`);
  }
};

// Servicios de evaluación
export const evaluationService = {
    submitEvaluation: (evaluationData) => {
        return axios.post(`${API_BASE_URL}/evaluations.php`, evaluationData);
    },
    getUserEvaluations: (userId) => {
        return axios.get(`${API_BASE_URL}/evaluations.php?user_id=${userId}`);
    },
    getEvaluationById: (id) => {
        return axios.get(`${API_BASE_URL}/evaluations.php?id=${id}`);
    },
    evaluateAnswer: (question, expectedAnswer, userAnswer) => {
        return axios.post(`${API_BASE_URL}/evaluate-answer.php`, {
            question,
            expectedAnswer,
            userAnswer
        });
    },

    // Nuevos servicios para administradores
  getAllEvaluations: () => {
    return axios.get(`${API_BASE_URL}/admin/evaluations.php`);
  },
  getEvaluationWithQuestions: (id) => {
    return axios.get(`${API_BASE_URL}/admin/evaluations.php?id=${id}&include_questions=true`);
  },
  createEvaluation: (evaluationData) => {
    return axios.post(`${API_BASE_URL}/admin/evaluations.php`, evaluationData);
  },
  updateEvaluation: (evaluationData) => {
    return axios.put(`${API_BASE_URL}/admin/evaluations.php`, evaluationData);
  },
  deleteEvaluation: (id) => {
    return axios.delete(`${API_BASE_URL}/admin/evaluations.php`, { 
      data: { id } 
    });
  },
  getScoreDistribution: () => {
    return axios.get(`${API_BASE_URL}/admin/evaluation-scores.php`);
  }

};

// Servicios de preguntas
export const questionService = {
    getQuestionsByModule: (moduleId) => {
        return axios.get(`${API_BASE_URL}/questions.php?module_id=${moduleId}`);
    }
};

// Servicios de autenticación
export const authService = {
    // Si el servidor pide código (cuenta del equipo en un proyecto sin contraseña), se abre la
    // ventana del código y se reintenta con él; quien llama recibe la respuesta final como siempre.
    login: async (credentials) => {
        const url = `${API_BASE_URL}/auth.php?action=login`;
        let res = await axios.post(url, credentials);
        if (!res.data?.requiere_codigo) return res;
        const { pedirCodigoIngreso } = await import('../components/CodigoIngresoModal');
        let mensaje = res.data.message;
        let error = '';
        for (;;) {
            const codigo = await pedirCodigoIngreso({ mensaje, email: res.data.email, error });
            if (!codigo) {
                const cancelado = new Error('Ingreso cancelado');
                cancelado.response = { status: 401, data: { message: 'Ingreso cancelado: falta el código que enviamos a tu correo.' } };
                throw cancelado;
            }
            try {
                if (codigo === '__reenviar__') {
                    res = await axios.post(url, credentials);
                    mensaje = res.data.message || mensaje;
                    error = '';
                    continue;
                }
                return await axios.post(url, { ...credentials, codigo });
            } catch (e) {
                if (!e.response?.data?.requiere_codigo && e.response?.status !== 429) throw e;
                error = e.response.data.message || 'Código incorrecto';
            }
        }
    },
    // Igual que login pero sin la ventana del código: quien llama pide el código en su
    // propio formulario (portada_acceso) y reintenta con `codigo`.
    loginPaso: (credentials) => {
        return axios.post(`${API_BASE_URL}/auth.php?action=login`, credentials);
    },
    register: (userData) => {
        return axios.post(`${API_BASE_URL}/auth.php?action=register`, userData);
    },
    logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    }
};

// Servicios de cursos
export const courseService = {
    getAllCourses: () => {
        return axios.get(`${API_BASE_URL}/courses.php`);
    },
    getCourseById: (id) => {
        return axios.get(`${API_BASE_URL}/courses.php?id=${id}`);
    },
    getModulesByCourse: (courseId) => {
        return axios.get(`${API_BASE_URL}/modules.php?course_id=${courseId}`);
    },

    // Nuevos servicios para administradores
  createCourse: (courseData) => {
    return axios.post(`${API_BASE_URL}/admin/courses.php`, courseData);
  },
  updateCourse: (courseData) => {
    return axios.put(`${API_BASE_URL}/admin/courses.php`, courseData);
  },
  deleteCourse: (id) => {
    return axios.delete(`${API_BASE_URL}/admin/courses.php`, { 
      data: { id } 
    });
  }

};

// Servicios de lecciones
export const lessonService = {
    getLessonsByCourse: (courseId) => {
        return axios.get(`${API_BASE_URL}/lessons.php?curso_id=${courseId}`);
    },
    getLessonById: (id) => {
        return axios.get(`${API_BASE_URL}/lessons.php?id=${id}`);
    },
    getLessonsByModule: (moduleId) => {
        return axios.get(`${API_BASE_URL}/lessons.php?module_id=${moduleId}`);
    },
    getNextActivity: (moduleId, currentLessonId) => {
        return axios.get(`${API_BASE_URL}/lessons.php?action=next_activity&module_id=${moduleId}&current_lesson_id=${currentLessonId}`);
      },
      
      
      markLessonCompleted: ({ user_id, lesson_id, completado = 1 }) => {
        // Si no hay user_id, intentar recuperarlo del localStorage
        if (!user_id) {
          try {
            const storedUser = JSON.parse(localStorage.getItem('user'));
            if (storedUser && storedUser.id) {
              console.log('Recuperando ID de usuario desde localStorage en api.js:', storedUser.id);
              user_id = storedUser.id;
            }
          } catch (e) {
            console.error('Error recuperando usuario desde localStorage en api.js:', e);
          }
        }
      
        // Convertir a números
        const userData = {
          user_id: user_id ? (typeof user_id === 'string' ? parseInt(user_id, 10) : user_id) : null,
          lesson_id: typeof lesson_id === 'string' ? parseInt(lesson_id, 10) : lesson_id,
          completado: typeof completado === 'string' ? parseInt(completado, 10) : completado
        };
      
        // Registrar datos enviados
        console.log('Enviando datos a progress.php:', userData);
      
        return axios.post(`${API_BASE_URL}/progress.php`, userData)
          .then(response => {
            console.log('Respuesta de progress.php:', response.data);
            return response;
          })
          .catch(error => {
            console.error('Error al marcar lección completada:', error);
            if (error.response) {
              console.error('Datos de respuesta del error:', error.response.data);
            }
            throw error;
          });
      }
      
};

// Servicios de voz
export const voiceService = {
    // Texto a voz
    speak: (text, options = {}) => {

          // 🔍 AGREGAR ESTAS LÍNEAS AQUÍ
    console.log('🔍 DEBUG API - options completo:', options);
    console.log('🔍 DEBUG API - options.sessionToken:', options.sessionToken);
    console.log('🔍 DEBUG API - typeof:', typeof options.sessionToken);

        return axios.post(`${API_BASE_URL}/tts.php`, { 
            text,
            forceService: options.forceService,
            voiceId: options.voiceId,
            sessionToken: options.sessionToken // 🆕 AGREGAR ESTA LÍNEA
        });
    },
    
    // Obtener voces disponibles
    getVoices: (service = 'polly') => {
        return axios.get(`${API_BASE_URL}/voices.php?service=${service}`);
    },
    
    // Interpretación de comandos con OpenAI
    interpretCommand: (command, context = {}) => {
        return axios.post(`${API_BASE_URL}/voice-command.php`, { command, context });
    }
};

export const completedCourseService = {
    saveCompletedCourse: (userId, cursoId) => {
      return axios.post(`${API_BASE_URL}/completed.php`, {
        user_id: userId,
        curso_id: cursoId
      });
    }
  };
  
  export const userService = {
    getAll: () => axios.get(`${API_BASE_URL}/users.php`),
    deleteUser: (id) => axios.delete(`${API_BASE_URL}/users.php`, { data: { id } }),
    updateUser: (data) => axios.put(`${API_BASE_URL}/users.php`, data),
    updateIdioma: (idioma) => axios.patch(`${API_BASE_URL}/users.php`, { idioma_preferido: idioma }),

    // Nuevos servicios para administradores
  createUser: (userData) => {
    return axios.post(`${API_BASE_URL}/admin/create-user.php`, userData);
  }

  };
 
  // src/services/api.js
// Agregar al final del archivo:

// Servicios de consulta
export const consultaService = {
  // Obtener información de un documento
  getDocumentById: (documentId) => {
    return axios.get(`${API_BASE_URL}/documentos.php?id=${documentId}`);
  },
  
  // Enviar una consulta. Timeout 90s — la generación del pool de evaluación
  // puede tardar; si excede mostramos error en lugar de spinner infinito.
  sendQuestion: (data) => {
    return axios.post(`${API_BASE_URL}/consulta.php`, data, { timeout: 90000 });
  },
  
  // Obtener lista de documentos disponibles
  getDocumentos: () => {
    return axios.get(`${API_BASE_URL}/documentos.php`);
  },
  
  // Crear un nuevo documento
  createDocumento: (formData) => {
    console.log("Enviando createDocumento a", `${API_BASE_URL}/documentos.php`);
    
    // Debug - listar contenido del FormData
    for (let pair of formData.entries()) {
      console.log(pair[0] + ': ' + (pair[0] === 'imagen' ? 'File object' : pair[1]));
    }
    
    return axios.post(`${API_BASE_URL}/documentos.php`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // Actualizar un documento existente
// Actualizar un documento existente
updateDocumento: (data) => {
  console.log('🔄 updateDocumento - Enviando PUT con datos:', data);
  return axios.put(`${API_BASE_URL}/documentos.php`, data, {
    headers: {
      'Content-Type': 'application/json'
    }
  });
},
  
  // Subir/actualizar imagen de un documento
  uploadDocumentoImagen: (documentId, file) => {
    const formData = new FormData();
    formData.append('imagen', file);
    return axios.patch(`${API_BASE_URL}/documentos.php?id=${documentId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Subir/actualizar logo de un documento
  uploadDocumentoLogo: (documentId, file) => {
    const formData = new FormData();
    formData.append('logo', file);
    return axios.patch(`${API_BASE_URL}/documentos.php?id=${documentId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Subir foto del docente → devuelve { url } (reutiliza el uploader de landing)
  uploadDocenteFoto: (file) => {
    const formData = new FormData();
    formData.append('proyecto_id', '1');
    formData.append('seccion_id', `docente_${Date.now()}`);
    formData.append('imagen', file);
    return axios.post(`${API_BASE_URL}/admin/landing-upload.php`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Eliminar un documento
  deleteDocumento: (id) => {
    return axios.delete(`${API_BASE_URL}/documentos.php?id=${id}`);
  },
  
  // Obtener progreso del modo mentor para un usuario
  getMentorProgress: (documentId, userId) => {
    return axios.get(`${API_BASE_URL}/mentor/progreso.php`, {
      params: {
        document_id: documentId,
        user_id: userId
      }
    });
  },

  // Obtener video de una lección específica
  getVideoForLesson: (documentId, moduleNumber, lessonNumber) => {
    return axios.get(`${API_BASE_URL}/mentor/video-leccion.php`, {
      params: {
        document_id: documentId,
        modulo: moduleNumber,
        leccion: lessonNumber
      }
    });
  }
};

// Servicios de anexos multimedia
export const attachmentService = {
  // Subir anexo
upload: (documentId, file, titulo, descripcion = '', keywords = '', transcripcion = '') => {
  const formData = new FormData();
  
  if (file) {
    formData.append('file', file);
  }
  formData.append('document_id', documentId);
  formData.append('titulo', titulo);
  formData.append('descripcion', descripcion);
  formData.append('keywords', keywords);
  formData.append('transcripcion', transcripcion); // ✅ AGREGAR ESTA LÍNEA

  return axios.post(`${API_BASE_URL}/anexos.php?action=upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: (progressEvent) => {
      const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      window.dispatchEvent(new CustomEvent('uploadProgress', { 
        detail: { progress, file: file ? file.name : 'video-vimeo' } 
      }));
    }
  });
},



  // Obtener anexos por documento
  getByDocument: (documentId, activeOnly = true) => {
    return axios.get(`${API_BASE_URL}/anexos.php?action=by_document&document_id=${documentId}&active_only=${activeOnly}`);
  },

  // Obtener anexo específico
  getById: (anexoId) => {
    return axios.get(`${API_BASE_URL}/anexos.php?action=by_id&id=${anexoId}`);
  },

  // Buscar anexos
  search: (documentId, keywords) => {
    return axios.get(`${API_BASE_URL}/anexos.php?action=search&document_id=${documentId}&keywords=${encodeURIComponent(keywords)}`);
  },

  // Actualizar anexo
  update: (anexoData) => {
    return axios.put(`${API_BASE_URL}/anexos.php`, anexoData);
  },

  // Eliminar anexo
  delete: (anexoId, hardDelete = false) => {
    return axios.delete(`${API_BASE_URL}/anexos.php?id=${anexoId}&hard=${hardDelete}`);
  },

  // Obtener estadísticas
  getStats: () => {
    return axios.get(`${API_BASE_URL}/anexos.php?action=stats`);
  },

  // Limpiar archivos huérfanos
  cleanup: () => {
    return axios.post(`${API_BASE_URL}/anexos.php?action=cleanup`);
  },

  // Descargar archivo
  download: (anexoId, isThumb = false) => {
    const url = `${API_BASE_URL}/anexos.php?action=download&id=${anexoId}&thumb=${isThumb}`;
    window.open(url, '_blank');
  },

  // Validar archivo antes de subir
  validateFile: (file, maxSizeMB = 5) => {
    const errors = [];
    
    // Validar tamaño
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      errors.push(`El archivo es demasiado grande. Máximo: ${maxSizeMB}MB`);
    }

    // Validar tipo
    const allowedTypes = {
      image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      document: ['application/pdf'],
      video: ['video/mp4', 'video/webm'],
      audio: ['audio/mpeg', 'audio/wav', 'audio/ogg']
    };

    const isValidType = Object.values(allowedTypes).flat().includes(file.type);
    if (!isValidType) {
      errors.push('Tipo de archivo no permitido');
    }

    return {
      isValid: errors.length === 0,
      errors: errors,
      fileType: Object.keys(allowedTypes).find(type => 
        allowedTypes[type].includes(file.type)
      ) || 'other'
    };
  }
};

// Servicios de transcripciones de video
export const transcriptionService = {
  // Guardar transcripción
  save: (videoId, transcripcion, formato = 'texto', timestamps = null) => {
    return axios.post(`${API_BASE_URL}/video-transcripciones.php?action=save`, {
      video_id: videoId,
      transcripcion: transcripcion,
      formato: formato,
      timestamps: timestamps
    });
  },

  // Obtener transcripción
  get: (videoId) => {
    return axios.get(`${API_BASE_URL}/video-transcripciones.php?video_id=${videoId}`);
  },

  // Procesar transcripción de texto con timestamps
  processTimestampText: (text) => {
    const lines = text.split('\n');
    const processed = {
      texto_limpio: '',
      timestamps: []
    };

    lines.forEach(line => {
      const match = line.match(/^(\d+:\d+:\d+\.\d+),(\d+:\d+:\d+\.\d+)(.+)$/);
      if (match) {
        const [, inicio, fin, texto] = match;
        processed.texto_limpio += texto.trim() + ' ';
        processed.timestamps.push({
          inicio: inicio,
          fin: fin,
          texto: texto.trim()
        });
      }
    });

    return processed;
  }
};

// Servicios de reto semanal
export const retoService = {
  // Verificar si hay reto pendiente para el usuario
  verificarRetoPendiente: (documentId, userId) => {
    return axios.get(`${API_BASE_URL}/reto.php`, {
      params: {
        action: 'verificar',
        document_id: documentId,
        user_id: userId
      }
    });
  },

  // Obtener historial de retos del usuario
  obtenerHistorial: (documentId, userId, limite = 10) => {
    return axios.get(`${API_BASE_URL}/reto.php`, {
      params: {
        action: 'historial',
        document_id: documentId,
        user_id: userId,
        limite: limite
      }
    });
  },

  // Enviar respuesta a un reto
  responderReto: (retoId, userId, respuesta) => {
    return axios.post(`${API_BASE_URL}/reto.php`, {
      action: 'responder',
      reto_id: retoId,
      user_id: userId,
      respuesta: respuesta
    });
  },

  // Obtener estadísticas de retos para admin
  obtenerEstadisticasAdmin: (documentId) => {
    return axios.get(`${API_BASE_URL}/reto.php`, {
      params: {
        action: 'admin_stats',
        document_id: documentId
      }
    });
  },

  // Generar reto manualmente (admin)
  generarReto: (documentId, diaReto = null) => {
    return axios.post(`${API_BASE_URL}/reto.php`, {
      action: 'generar',
      document_id: documentId,
      dia_reto: diaReto
    });
  },

  // Obtener detalle de un reto específico con todas sus respuestas
  obtenerDetalleReto: (retoId) => {
    return axios.get(`${API_BASE_URL}/reto.php`, {
      params: {
        action: 'reto_detalle',
        reto_id: retoId
      }
    });
  }
};

// Reto Realtime — sesión OpenAI Realtime para 1 pregunta semanal
export const retoRealtimeService = {
  iniciar: (documentId) => {
    return axios.post(`${API_BASE_URL}/reto-realtime/iniciar.php`, {
      document_id: documentId,
    });
  },
};

// Servicio de notificaciones
export const notificacionService = {
  // Admin
  getAll: () => axios.get(`${API_BASE_URL}/admin/notificaciones.php`),
  create: (data) => axios.post(`${API_BASE_URL}/admin/notificaciones.php`, data),
  update: (data) => axios.put(`${API_BASE_URL}/admin/notificaciones.php`, data),
  delete: (id) => axios.delete(`${API_BASE_URL}/admin/notificaciones.php?id=${id}`),
  // Usuario
  getPendientes: () => axios.get(`${API_BASE_URL}/notificaciones.php`),
  marcarLeida: (notificacionId) => axios.post(`${API_BASE_URL}/notificaciones.php`, { notificacion_id: notificacionId }),
};

// Servicio de Seguimiento (Cohortes, Contactos, Matrículas)
// Seguimiento (Fase 17): TODO va dentro de un proyecto. El backend rechaza con
// 400 cualquier llamada sin proyecto_id — es lo que impide que un cliente vea
// los contactos de otro. Por eso `proyectoId` es el primer parámetro en todos
// los métodos y se inyecta en la query o en el cuerpo.
const qs = (proyectoId, params = {}) =>
  new URLSearchParams({ proyecto_id: proyectoId, ...params }).toString();

export const seguimientoService = {
  // Cohortes
  getCohortes: (proyectoId) => axios.get(`${API_BASE_URL}/admin/cohortes.php?${qs(proyectoId)}`),
  getCohorte: (proyectoId, id) => axios.get(`${API_BASE_URL}/admin/cohortes.php?${qs(proyectoId, { id })}`),
  createCohorte: (proyectoId, data) => axios.post(`${API_BASE_URL}/admin/cohortes.php`, { proyecto_id: proyectoId, ...data }),
  updateCohorte: (proyectoId, data) => axios.put(`${API_BASE_URL}/admin/cohortes.php`, { proyecto_id: proyectoId, ...data }),
  deleteCohorte: (proyectoId, id) => axios.delete(`${API_BASE_URL}/admin/cohortes.php?${qs(proyectoId, { id })}`),

  // Contactos
  getContactos: (proyectoId, params = {}) =>
    axios.get(`${API_BASE_URL}/admin/contactos.php?${qs(proyectoId, params)}`),
  createContacto: (proyectoId, data) => axios.post(`${API_BASE_URL}/admin/contactos.php`, { proyecto_id: proyectoId, ...data }),
  updateContacto: (proyectoId, data) => axios.put(`${API_BASE_URL}/admin/contactos.php`, { proyecto_id: proyectoId, ...data }),
  deleteContacto: (proyectoId, id) => axios.delete(`${API_BASE_URL}/admin/contactos.php?${qs(proyectoId, { id })}`),
  importarContactos: (proyectoId, data) =>
    axios.post(`${API_BASE_URL}/admin/contactos.php`, { proyecto_id: proyectoId, importar: true, ...data }),
  // Importar desde Excel/CSV con revisión e invitación programada (contactos-importar.php)
  analizarContactos: (proyectoId, cohorteId, archivo, mapeo) => {
    const fd = new FormData();
    fd.append('accion', 'analizar');
    fd.append('proyecto_id', proyectoId);
    fd.append('cohorte_id', cohorteId || 0);
    fd.append('archivo', archivo);
    if (mapeo) fd.append('mapeo', JSON.stringify(mapeo));
    return axios.post(`${API_BASE_URL}/admin/contactos-importar.php`, fd);
  },
  importarContactosRevisados: (proyectoId, data) =>
    axios.post(`${API_BASE_URL}/admin/contactos-importar.php`, { proyecto_id: proyectoId, accion: 'importar', ...data }),
  getCargasProgramadas: (proyectoId) => axios.get(`${API_BASE_URL}/admin/contactos-importar.php?${qs(proyectoId)}`),
  getPersonasCarga: (proyectoId, lote, params = {}) =>
    axios.get(`${API_BASE_URL}/admin/contactos-importar.php?${qs(proyectoId, { accion: 'personas', lote, ...params })}`),
  reprogramarCarga: (proyectoId, lote, fechaInvitacion) =>
    axios.post(`${API_BASE_URL}/admin/contactos-importar.php`, { proyecto_id: proyectoId, accion: 'reprogramar', lote, fecha_invitacion: fechaInvitacion }),
  cancelarCarga: (proyectoId, lote) =>
    axios.post(`${API_BASE_URL}/admin/contactos-importar.php`, { proyecto_id: proyectoId, accion: 'cancelar', lote }),
  borrarCarga: (proyectoId, lote) =>
    axios.post(`${API_BASE_URL}/admin/contactos-importar.php`, { proyecto_id: proyectoId, accion: 'borrar', lote }),

  // Matrículas
  getMatriculas: (proyectoId, params = {}) =>
    axios.get(`${API_BASE_URL}/admin/matriculas.php?${qs(proyectoId, params)}`),
  createMatricula: (proyectoId, data) => axios.post(`${API_BASE_URL}/admin/matriculas.php`, { proyecto_id: proyectoId, ...data }),
  updateMatricula: (proyectoId, data) => axios.put(`${API_BASE_URL}/admin/matriculas.php`, { proyecto_id: proyectoId, ...data }),
  deleteMatricula: (proyectoId, id) => axios.delete(`${API_BASE_URL}/admin/matriculas.php?${qs(proyectoId, { id })}`),
  asignarMasivo: (proyectoId, data) =>
    axios.post(`${API_BASE_URL}/admin/matriculas.php`, { proyecto_id: proyectoId, asignar_masivo: true, ...data }),

  // Reglas de recordatorio
  getReglas: (proyectoId, cohorteId) =>
    axios.get(`${API_BASE_URL}/admin/reglas-recordatorio.php?${qs(proyectoId, { cohorte_id: cohorteId })}`),
  updateReglas: (proyectoId, data) =>
    axios.put(`${API_BASE_URL}/admin/reglas-recordatorio.php`, { proyecto_id: proyectoId, ...data }),
  // Agregar o quitar un recordatorio desde la línea de tiempo. Quitar uno que ya
  // envió correos lo desactiva en vez de borrarlo.
  crearRegla: (proyectoId, data) =>
    axios.post(`${API_BASE_URL}/admin/reglas-recordatorio.php`, { proyecto_id: proyectoId, ...data }),
  eliminarRegla: (proyectoId, id) =>
    axios.delete(`${API_BASE_URL}/admin/reglas-recordatorio.php?${qs(proyectoId, { id })}`),

  // Plantillas. Las que llegan con es_base=true son del sistema: se ven en todos
  // los proyectos pero no se editan ni se borran.
  getPlantillas: (proyectoId, params = {}) =>
    axios.get(`${API_BASE_URL}/admin/plantillas-mensaje.php?${qs(proyectoId, params)}`),
  createPlantilla: (proyectoId, data) => axios.post(`${API_BASE_URL}/admin/plantillas-mensaje.php`, { proyecto_id: proyectoId, ...data }),
  updatePlantilla: (proyectoId, data) => axios.put(`${API_BASE_URL}/admin/plantillas-mensaje.php`, { proyecto_id: proyectoId, ...data }),
  deletePlantilla: (proyectoId, id) => axios.delete(`${API_BASE_URL}/admin/plantillas-mensaje.php?${qs(proyectoId, { id })}`),

  // Estadísticas: incluye el panel de envíos (enviados, rebotes, quejas, fallidos)
  getStats: (proyectoId, params = {}) =>
    axios.get(`${API_BASE_URL}/admin/seguimiento-stats.php?${qs(proyectoId, params)}`),

  // Encender los recordatorios automáticos + lista «¿está todo listo?».
  getConfig: (proyectoId) =>
    axios.get(`${API_BASE_URL}/admin/seguimiento-config.php?${qs(proyectoId)}`),
  saveConfig: (proyectoId, data) =>
    axios.put(`${API_BASE_URL}/admin/seguimiento-config.php`, { proyecto_id: proyectoId, ...data }),

  // Programas preestructurados (Fase 17.5). Al aplicar uno, sus plantillas y
  // reglas se copian al proyecto: a partir de ahí son suyas y se editan.
  getProgramasBase: (proyectoId) =>
    axios.get(`${API_BASE_URL}/admin/seguimiento-programas.php?${qs(proyectoId)}`),
  aplicarPrograma: (proyectoId, data) =>
    axios.post(`${API_BASE_URL}/admin/seguimiento-programas.php`, { proyecto_id: proyectoId, ...data }),

  // Documentos (para selector de cohortes)
  getDocumentos: () => axios.get(`${API_BASE_URL}/documentos.php`),
};

// Comunicados (Fase 17.7): envíos puntuales a los estudiantes del proyecto, p. ej.
// «actualizamos la plataforma». Para mandar a todos hace falta una prueba con el
// texto actual y escribir el número de destinatarios; el backend además no encola
// nada si el servidor de correo rechaza la credencial.
// Flujo automático del proyecto: los correos por momento del recorrido, sin cohortes.
export const seguimientoFlujoService = {
  get: (proyectoId, cohorteId = null) =>
    axios.get(`${API_BASE_URL}/admin/seguimiento-flujo.php?proyecto_id=${proyectoId}${cohorteId ? `&cohorte_id=${cohorteId}` : ''}`),
  accion: (proyectoId, accion, data = {}) =>
    axios.put(`${API_BASE_URL}/admin/seguimiento-flujo.php`, { proyecto_id: proyectoId, accion, ...data }),
};

export const comunicadosService = {
  list: (proyectoId) =>
    axios.get(`${API_BASE_URL}/admin/comunicados.php?proyecto_id=${proyectoId}`),
  get: (proyectoId, id) =>
    axios.get(`${API_BASE_URL}/admin/comunicados.php?proyecto_id=${proyectoId}&id=${id}`),
  opciones: (proyectoId) =>
    axios.get(`${API_BASE_URL}/admin/comunicados.php?proyecto_id=${proyectoId}&accion=opciones`),
  // Buscar estudiantes del proyecto para elegirlos uno a uno.
  buscar: (proyectoId, q) =>
    axios.get(`${API_BASE_URL}/admin/comunicados.php?proyecto_id=${proyectoId}&accion=buscar&q=${encodeURIComponent(q)}`),
  accion: (proyectoId, accion, data = {}) =>
    axios.post(`${API_BASE_URL}/admin/comunicados.php`, { proyecto_id: proyectoId, accion, ...data }),
};

// Direcciones a las que no se les escribe (rebotes, quejas, bajas y bloqueos a mano).
export const supresionesService = {
  list: (proyectoId, params = {}) =>
    axios.get(`${API_BASE_URL}/admin/supresiones.php?${new URLSearchParams({ proyecto_id: proyectoId, ...params })}`),
  agregar: (proyectoId, texto, detalle) =>
    axios.post(`${API_BASE_URL}/admin/supresiones.php`, { proyecto_id: proyectoId, accion: 'agregar', texto, detalle }),
  quitar: (proyectoId, id, confirmar = false) =>
    axios.post(`${API_BASE_URL}/admin/supresiones.php`, { proyecto_id: proyectoId, accion: 'quitar', id, confirmar }),
};

// Identidad de correo del proyecto (Fase 17.2). La contraseña SMTP se guarda
// cifrada y nunca vuelve en el GET: `tiene_password` dice si hay una guardada, y
// enviarla vacía en el PUT la conserva.
export const proyectoEmailService = {
  // recomprobar=true se salta la caché de 10 min de la verificación SMTP.
  get: (proyectoId, recomprobar = false) =>
    axios.get(`${API_BASE_URL}/admin/proyecto-email.php?proyecto_id=${proyectoId}${recomprobar ? '&recomprobar=1' : ''}`),
  save: (proyectoId, data) =>
    axios.put(`${API_BASE_URL}/admin/proyecto-email.php`, { proyecto_id: proyectoId, ...data }),
  probar: (proyectoId, email) =>
    axios.post(`${API_BASE_URL}/admin/proyecto-email.php`, { proyecto_id: proyectoId, prueba_a: email }),
};

// Número de WhatsApp del proyecto (API pública de Operatix).
export const proyectoWhatsAppService = {
  get: (proyectoId) =>
    axios.get(`${API_BASE_URL}/admin/proyecto-whatsapp.php?proyecto_id=${proyectoId}`),
  save: (proyectoId, data) =>
    axios.put(`${API_BASE_URL}/admin/proyecto-whatsapp.php`, { proyecto_id: proyectoId, ...data }),
  probar: (proyectoId, apiKey) =>
    axios.post(`${API_BASE_URL}/admin/proyecto-whatsapp.php`, { proyecto_id: proyectoId, api_key: apiKey || '' }),
  // Registra en Operatix a dónde mandar respuestas y estados, y guarda su secreto.
  webhook: (proyectoId) =>
    axios.post(`${API_BASE_URL}/admin/proyecto-whatsapp.php`, { proyecto_id: proyectoId, accion: 'webhook' }),
};

// Servicio Mentor 2.0 (LMS híbrido)
// Planes de formación (Learning): qué cursos ve el alumno según su subrol y su avance en el plan.
export const miPlanService = {
  get: (detalle = false) => axios.get(`${API_BASE_URL}/mi-plan.php`, { params: detalle ? { detalle: 1 } : {} }),
};

export const mentor2Service = {
  // Interactivos SCORM: qué versión del paquete abrir y lo que informa (commit/finish).
  scormEstado: (mediaId) => axios.get(`${API_BASE_URL}/mentor/scorm.php`, { params: { media_id: mediaId } }),
  scormGuardar: (datos) => axios.post(`${API_BASE_URL}/mentor/scorm.php`, datos),
  start: (documentId) =>
    axios.get(`${API_BASE_URL}/mentor/start.php`, { params: { document_id: documentId } }),

  advance: (documentId) =>
    axios.post(`${API_BASE_URL}/mentor/advance.php`, { document_id: documentId }),

  // moduloActual/leccionActual (posición, 1-based) le sirven al tutor para saber
  // en qué punto del programa está el alumno cuando pregunta.
  chat: ({ documentId, videoId, pregunta, currentTime, moduloActual, leccionActual }) =>
    axios.post(`${API_BASE_URL}/mentor/chat.php`, {
      document_id: documentId,
      video_id: videoId,
      pregunta,
      current_time: currentTime,
      modulo_actual: moduloActual,
      leccion_actual: leccionActual
    }),

  // Preguntas sugeridas de la lección (se generan una vez por lección y quedan
  // guardadas; si no hay material, devuelve lista vacía sin error).
  sugerencias: (documentId, videoId) =>
    axios.get(`${API_BASE_URL}/mentor/sugerencias.php`, {
      params: { document_id: documentId, video_id: videoId }
    }),

  generateQuiz: (documentId, videoId) =>
    axios.post(`${API_BASE_URL}/mentor/quiz.php`, {
      action: 'generate',
      document_id: documentId,
      video_id: videoId
    }),

  evaluateQuiz: (documentId, videoId, numeroPregunta, respuesta) =>
    axios.post(`${API_BASE_URL}/mentor/quiz.php`, {
      action: 'evaluate',
      document_id: documentId,
      video_id: videoId,
      numero_pregunta: numeroPregunta,
      respuesta
    }),

  saveVideoProgress: (data) =>
    axios.post(`${API_BASE_URL}/video-mentor-progress.php`, data),

  // Aula: «Marcar como completado» una lección (solo si el proyecto lo permite).
  marcarLeccion: (documentId, moduloNumero, leccionNumero) =>
    axios.post(`${API_BASE_URL}/mentor/marcar-leccion.php`, {
      document_id: documentId, modulo_numero: moduloNumero, leccion_numero: leccionNumero,
    }),

  getVideoForLesson: (documentId, moduleNumber, lessonNumber) =>
    axios.get(`${API_BASE_URL}/mentor/video-leccion.php`, {
      params: { document_id: documentId, modulo: moduleNumber, leccion: lessonNumber }
    }),

  // Progreso % del usuario autenticado para varios documentos (fichas de curso
  // del landing). Con `porModulo` la respuesta agrega el avance de cada módulo.
  // MODO PRUEBAS: ¿el admin marcó a este usuario como usuario de pruebas?
  estadoPruebas: () =>
    axios.get(`${API_BASE_URL}/mentor/completar-prueba.php`),

  // MODO PRUEBAS: da por terminada una lección/módulo/curso/evaluación para el
  // usuario autenticado. El backend solo lo permite si el admin lo marcó como
  // usuario de pruebas.
  // Con `limpiar` hace lo contrario: borra el avance de ese alcance y lo deja
  // como si no se hubiera empezado.
  completarPrueba: ({ documentId, alcance, moduloNumero, leccionNumero, moduloId, limpiar }) =>
    axios.post(`${API_BASE_URL}/mentor/completar-prueba.php`, {
      document_id: documentId,
      alcance,
      ...(moduloId ? { modulo_id: moduloId } : {}),
      ...(moduloNumero ? { modulo_numero: moduloNumero } : {}),
      ...(leccionNumero ? { leccion_numero: leccionNumero } : {}),
      ...(limpiar ? { limpiar: true } : {}),
    }),

  // `incluirExamen`: en los cursos con evaluación, el examen cuenta como un
  // paso más y el 100% exige aprobarlo.
  progresoProyecto: (documentIds, { porModulo = false, incluirExamen = false, detalle = false } = {}) =>
    axios.get(`${API_BASE_URL}/mentor/progreso-proyecto.php`, {
      params: {
        document_ids: (documentIds || []).join(','),
        ...(detalle ? { detalle: 1 } : {}),
        ...(porModulo ? { por_modulo: 1 } : {}),
        ...(incluirExamen ? { incluir_examen: 1 } : {}),
      }
    }),
};

// Servicio de sesión Realtime (OpenAI Realtime API)
export const realtimeSessionService = {
  createSession: (documentId, mode = 'consulta') => {
    return axios.post(`${API_BASE_URL}/realtime-session.php`, {
      document_id: documentId,
      mode: mode
    });
  },
  saveTranscripts: (documentId, transcripts, durationSeconds, mode = 'consulta') => {
    return axios.post(`${API_BASE_URL}/save-realtime-transcripts.php`, {
      document_id: documentId,
      transcripts: transcripts,
      duration_seconds: durationSeconds,
      mode: mode
    });
  }
};

export const practicaService = {
  evaluate: (documentId, doctorId, transcript, durationSeconds) => {
    return axios.post(`${API_BASE_URL}/practica-evaluar.php`, {
      document_id: documentId,
      doctor_id: doctorId,
      transcript: transcript,
      duration_seconds: durationSeconds
    });
  }
};

export const convenioService = {
  list: (proyectoId) => axios.get(`${API_BASE_URL}/admin/convenios.php?proyecto_id=${proyectoId}`),
  listAll: () => axios.get(`${API_BASE_URL}/admin/convenios.php?all=1`),
  create: (data) => axios.post(`${API_BASE_URL}/admin/convenios.php`, data),
  update: (data) => axios.put(`${API_BASE_URL}/admin/convenios.php`, data),
  delete: (id) => axios.delete(`${API_BASE_URL}/admin/convenios.php?id=${id}`)
};

export const especialidadService = {
  list: () => axios.get(`${API_BASE_URL}/admin/especialidades.php`)
};

export const paisService = {
  list: () => axios.get(`${API_BASE_URL}/admin/paises.php`)
};

// Accesos externos: enlaces que otra plataforma reparte para entrar directo a
// un curso. El token vive en la base, no en la config pública del proyecto.
// «¿Has olvidado tu contraseña?» — público. El proyecto lo detecta el backend por
// el dominio, y con él el remitente del correo y el dominio del enlace.
// Contenido interactivo (paquetes HTML5 de Articulate/SCORM) para lecciones del modo Mentor.
// Centro de alertas del alumno (campana del encabezado + /alertas). Backend: api/alertas.php.
export const alertasService = {
  panel: (proyecto_id) => axios.get(`${API_BASE_URL}/alertas.php`, { params: { proyecto_id, panel: 1 } }),
  contador: (proyecto_id) => axios.get(`${API_BASE_URL}/alertas.php`, { params: { proyecto_id, contador: 1 } }),
  historial: (proyecto_id, { tipo, q, pagina, estado, dias } = {}) => axios.get(`${API_BASE_URL}/alertas.php`, { params: { proyecto_id, tipo, q, pagina, estado, dias } }),
  leer: (proyecto_id, ids) => axios.post(`${API_BASE_URL}/alertas.php`, { accion: 'leer', proyecto_id, ids }),
  quitar: (proyecto_id, id) => axios.post(`${API_BASE_URL}/alertas.php`, { accion: 'quitar', proyecto_id, id }),
  limpiar: (proyecto_id) => axios.post(`${API_BASE_URL}/alertas.php`, { accion: 'limpiar', proyecto_id }),
  borrar: (proyecto_id, ids) => axios.post(`${API_BASE_URL}/alertas.php`, { accion: 'borrar', proyecto_id, ids }),
  clic: (proyecto_id, id) => axios.post(`${API_BASE_URL}/alertas.php`, { accion: 'clic', proyecto_id, id }),
};

export const interactivosService = {
  list: () => axios.get(`${API_BASE_URL}/admin/interactivos.php`),
  subirZip: (file) => {
    const fd = new FormData();
    fd.append('archivo', file);
    return axios.post(`${API_BASE_URL}/admin/interactivos.php`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // El servidor acepta 2 MB por envío: el .zip se manda en partes de 1,5 MB (con reintentos).
  subirZipPorPartes: async (file, alAvanzar) => {
    const TAM = 1.5 * 1024 * 1024;
    const total = Math.max(1, Math.ceil(file.size / TAM));
    const subida = Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
    let res;
    for (let i = 0; i < total; i++) {
      const fd = new FormData();
      fd.append('accion', 'trozo'); fd.append('subida', subida); fd.append('indice', i);
      fd.append('total', total); fd.append('nombre', file.name);
      fd.append('trozo', file.slice(i * TAM, (i + 1) * TAM), 'trozo');
      for (let intento = 1; ; intento++) {
        try {
          res = await axios.post(`${API_BASE_URL}/admin/interactivos.php`, fd, { timeout: i === total - 1 ? 600000 : 120000 });
          break;
        } catch (e) {
          if (intento >= 3 || (e.response && e.response.status < 500 && e.response.status !== 408)) throw e;
          await new Promise(r => setTimeout(r, 1500 * intento));
        }
      }
      alAvanzar && alAvanzar((i + 1) / total);
    }
    return res;
  },
  analizar: (ruta) => axios.get(`${API_BASE_URL}/admin/interactivos.php`, { params: { analizar: ruta } }),
  registrar: ({ ruta, titulo, descripcion, proyecto_id }) =>
    axios.post(`${API_BASE_URL}/admin/interactivos.php`, { accion: 'registrar', ruta, titulo, descripcion, proyecto_id }),
  youtube: ({ url, titulo, descripcion, proyecto_id }) =>
    axios.post(`${API_BASE_URL}/admin/interactivos.php`, { accion: 'youtube', url, titulo, descripcion, proyecto_id }),
  // Lectura (texto que el reproductor muestra como contenido); con id la edita.
  lectura: ({ id, titulo, texto, proyecto_id }) =>
    axios.post(`${API_BASE_URL}/admin/interactivos.php`, { accion: 'lectura', id, titulo, texto, proyecto_id }),
};

export const recuperarContrasenaService = {
  solicitar: (identificador) =>
    axios.post(`${API_BASE_URL}/recuperar-contrasena.php`, { accion: 'solicitar', identificador }),
  validar: (token) =>
    axios.post(`${API_BASE_URL}/recuperar-contrasena.php`, { accion: 'validar', token }),
  cambiar: (token, password) =>
    axios.post(`${API_BASE_URL}/recuperar-contrasena.php`, { accion: 'cambiar', token, password }),
};

// «Entrar como» un usuario (admin → pestaña nueva en el dominio de su proyecto).
export const suplantacionService = {
  // Admin: proyectos donde puede entrar + historial de usos.
  info: (userId) =>
    axios.get(`${API_BASE_URL}/admin/suplantar.php?user_id=${userId}`),
  // Admin: pase de un solo uso → { url }
  crear: (userId, proyectoId, guardarAvance) =>
    axios.post(`${API_BASE_URL}/admin/suplantar.php`, { user_id: userId, proyecto_id: proyectoId, guardar_avance: guardarAvance }),
  // Público: lo llama /suplantar/:pase en el dominio del proyecto.
  canjear: (pase) =>
    axios.post(`${API_BASE_URL}/suplantar.php`, { pase }),
  salir: () =>
    axios.post(`${API_BASE_URL}/suplantar.php`, { accion: 'salir' }),
};

export const accesoExternoService = {
  // Público: lo llama la pantalla /acceso/:slug/:token con lo que traiga la URL.
  entrar: (slug, token, params) =>
    axios.post(`${API_BASE_URL}/acceso-externo.php`, { slug, token, params }),
  // Admin
  listar: (proyectoId) =>
    axios.get(`${API_BASE_URL}/admin/enlaces-externos.php?proyecto_id=${proyectoId}`),
  estadisticas: (proyectoId) =>
    axios.get(`${API_BASE_URL}/admin/enlaces-externos.php?proyecto_id=${proyectoId}&accion=stats`),
  crear: (data) => axios.post(`${API_BASE_URL}/admin/enlaces-externos.php`, data),
  actualizar: (data) => axios.put(`${API_BASE_URL}/admin/enlaces-externos.php`, data),
  eliminar: (id) => axios.delete(`${API_BASE_URL}/admin/enlaces-externos.php?id=${id}`),
};

// Importador de datos por proyecto (Fase 1: usuarios)
export const importService = {
  esquema: (proyectoId) =>
    axios.get(`${API_BASE_URL}/admin/importar-usuarios.php?action=esquema&proyecto_id=${proyectoId}`),
  lotes: (proyectoId) =>
    axios.get(`${API_BASE_URL}/admin/importar-usuarios.php?action=lotes&proyecto_id=${proyectoId}`),
  analizar: (proyectoId, archivo) => {
    const fd = new FormData();
    fd.append('proyecto_id', proyectoId);
    fd.append('archivo', archivo);
    return axios.post(`${API_BASE_URL}/admin/importar-usuarios.php?action=analizar`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  previsualizar: (data) =>
    axios.post(`${API_BASE_URL}/admin/importar-usuarios.php?action=previsualizar`, data),
  ejecutar: (data) =>
    axios.post(`${API_BASE_URL}/admin/importar-usuarios.php?action=ejecutar`, data),
  cancelar: (loteId) =>
    axios.post(`${API_BASE_URL}/admin/importar-usuarios.php?action=cancelar`, { lote_id: loteId }),
};

// Contenidos asignados a un proyecto (tabla proyecto_documentos). Va aparte del
// guardado del proyecto: asignar o quitar aquí no toca nada más de la ficha.
export const proyectoContenidoService = {
  list: (proyectoId) => axios.get(`${API_BASE_URL}/admin/proyecto-contenidos.php`, { params: { proyecto_id: proyectoId } }),
  asignar: (proyectoId, documentoIds) => axios.post(`${API_BASE_URL}/admin/proyecto-contenidos.php`, {
    proyecto_id: proyectoId, documento_ids: documentoIds
  }),
  ordenar: (proyectoId, documentoIds) => axios.put(`${API_BASE_URL}/admin/proyecto-contenidos.php`, {
    proyecto_id: proyectoId, documento_ids: documentoIds
  }),
  quitar: (proyectoId, documentoId) => axios.delete(`${API_BASE_URL}/admin/proyecto-contenidos.php`, {
    params: { proyecto_id: proyectoId, documento_id: documentoId }
  }),
};

export const proyectoService = {
  // resumen = true trae las cifras de cada proyecto para la lista del panel.
  list: (resumen = false) => axios.get(`${API_BASE_URL}/admin/proyectos.php`, resumen ? { params: { resumen: 1 } } : undefined),
  get: (id) => axios.get(`${API_BASE_URL}/admin/proyectos.php?id=${id}`),
  // Cómo va un proyecto, qué le falta y el estado de cada sección de su ficha.
  resumen: (id) => axios.get(`${API_BASE_URL}/admin/proyecto-resumen.php`, { params: { id } }),
  create: (formData) => axios.post(`${API_BASE_URL}/admin/proyectos.php`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (formData) => {
    return axios.post(`${API_BASE_URL}/admin/proyectos.php`, formData, {
      headers: { 'Content-Type': 'multipart/form-data', 'X-HTTP-Method-Override': 'PUT' }
    });
  },
  updateJSON: (data) => axios.put(`${API_BASE_URL}/admin/proyectos.php`, data),
  dashboardExcluidos: (data) => axios.post(`${API_BASE_URL}/admin/dashboard-excluidos.php`, data),
  conectarDominio: (proyectoId) => axios.post(`${API_BASE_URL}/admin/conectar-dominio.php`, { proyecto_id: proyectoId }),
  delete: (id) => axios.delete(`${API_BASE_URL}/admin/proyectos.php?id=${id}`),
  getInfo: () => axios.get(`${API_BASE_URL}/proyecto-info.php`),
  saveLandingSections: (proyectoId, secciones) => {
    return axios.put(`${API_BASE_URL}/admin/proyectos.php`, {
      id: proyectoId,
      config_json: { landing_secciones: secciones }
    });
  },
  uploadLandingImage: (proyectoId, seccionId, file) => {
    const formData = new FormData();
    formData.append('proyecto_id', proyectoId);
    formData.append('seccion_id', seccionId);
    formData.append('imagen', file);
    return axios.post(`${API_BASE_URL}/admin/landing-upload.php`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteLandingImage: (proyectoId, path) => {
    const formData = new FormData();
    formData.append('action', 'delete');
    formData.append('proyecto_id', proyectoId);
    formData.append('path', path);
    return axios.post(`${API_BASE_URL}/admin/landing-upload.php`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Miembros
  getMiembros: (proyectoId) =>
    axios.get(`${API_BASE_URL}/admin/proyectos.php?action=miembros&proyecto_id=${proyectoId}`),
  addMiembro: (data) =>
    axios.post(`${API_BASE_URL}/admin/proyectos.php?action=miembros`, data),
  removeMiembro: (id) =>
    axios.delete(`${API_BASE_URL}/admin/proyectos.php?action=miembros&id=${id}`),
  buscarUsuarios: (query) =>
    axios.get(`${API_BASE_URL}/admin/proyectos.php?action=buscar_usuarios&q=${encodeURIComponent(query)}`),
};

// ============ Aceptación obligatoria de términos (modal post-login) ============
export const aceptacionService = {
  // ¿El usuario ya aceptó la versión vigente de este proyecto?
  estado: (proyectoId, version) =>
    axios.get(`${API_BASE_URL}/aceptacion.php?proyecto_id=${proyectoId}${version ? `&version=${version}` : ''}`),
  aceptar: (data) => axios.post(`${API_BASE_URL}/aceptacion.php`, data),
  // Admin: quién aceptó y cuándo.
  listado: (proyectoId) =>
    axios.get(`${API_BASE_URL}/aceptacion.php?proyecto_id=${proyectoId}&listado=1`),
};

// ============ Solicitudes de acceso (prerregistro desde la landing) ============
export const solicitudAccesoService = {
  // Público: el formulario «Solicitar acceso al programa». No crea cuenta.
  enviar: (data) => axios.post(`${API_BASE_URL}/solicitud-acceso.php`, data),
  // Admin
  listar: (proyectoId, filtros = {}) => {
    const p = new URLSearchParams({ proyecto_id: proyectoId });
    if (filtros.estado) p.set('estado', filtros.estado);
    if (filtros.q) p.set('q', filtros.q);
    return axios.get(`${API_BASE_URL}/admin/solicitudes-acceso.php?${p.toString()}`);
  },
  exportarCsv: (proyectoId) =>
    axios.get(`${API_BASE_URL}/admin/solicitudes-acceso.php?proyecto_id=${proyectoId}&formato=csv`, { responseType: 'blob' }),
  actualizar: (id, cambios) => axios.put(`${API_BASE_URL}/admin/solicitudes-acceso.php`, { id, ...cambios }),
  guardarConfig: (proyectoId, config) =>
    axios.put(`${API_BASE_URL}/admin/solicitudes-acceso.php`, { proyecto_id: proyectoId, config }),
  probarAviso: (proyectoId) =>
    axios.post(`${API_BASE_URL}/admin/solicitudes-acceso.php`, { proyecto_id: proyectoId, accion: 'probar_aviso' }),
  crearUsuario: (id) => axios.post(`${API_BASE_URL}/admin/solicitudes-acceso.php`, { id, accion: 'crear_usuario' }),
  eliminar: (id) => axios.delete(`${API_BASE_URL}/admin/solicitudes-acceso.php?id=${id}`),
};

// ============ Hub de contenido (artículos tipo blog) ============
export const articuloService = {
  // Público
  listar: (proyectoId, opts = {}) =>
    axios.get(`${API_BASE_URL}/articulos.php`, { params: { proyecto_id: proyectoId, ...opts } }),
  obtener: (proyectoId, slug) =>
    axios.get(`${API_BASE_URL}/articulos.php`, { params: { proyecto_id: proyectoId, slug } }),
  // Admin
  adminListar: (proyectoId, coleccion = 'hub') =>
    axios.get(`${API_BASE_URL}/admin/articulos.php`, { params: { proyecto_id: proyectoId, coleccion } }),
  adminObtener: (id) => axios.get(`${API_BASE_URL}/admin/articulos.php`, { params: { id } }),
  crear: (data) => axios.post(`${API_BASE_URL}/admin/articulos.php`, data),
  actualizar: (id, data) => axios.put(`${API_BASE_URL}/admin/articulos.php`, { id, ...data }),
  eliminar: (id) => axios.delete(`${API_BASE_URL}/admin/articulos.php?id=${id}`),
  guardarCategoria: (data) => axios.post(`${API_BASE_URL}/admin/articulos.php`, { accion: 'categoria', ...data }),
  eliminarCategoria: (id) => axios.delete(`${API_BASE_URL}/admin/articulos.php?categoria_id=${id}`),
  ordenarCategorias: (ids) => axios.post(`${API_BASE_URL}/admin/articulos.php`, { accion: 'orden_categorias', ids }),
};

// ============ Biblioteca de bloques por proyecto (Divi-like) ============
export const bloqueService = {
  // Lista/busca bloques del proyecto. opts: { q, tipo }
  list: (proyectoId, opts = {}) => {
    const p = new URLSearchParams({ proyecto_id: proyectoId });
    if (opts.q) p.set('q', opts.q);
    if (opts.tipo) p.set('tipo', opts.tipo);
    return axios.get(`${API_BASE_URL}/admin/proyecto-bloques.php?${p.toString()}`);
  },
  save: (data) => axios.post(`${API_BASE_URL}/admin/proyecto-bloques.php`, data),
  usar: (id) => axios.post(`${API_BASE_URL}/admin/proyecto-bloques.php?action=usar`, { id }),
  update: (id, data) => axios.put(`${API_BASE_URL}/admin/proyecto-bloques.php`, { id, ...data }),
  remove: (id) => axios.delete(`${API_BASE_URL}/admin/proyecto-bloques.php?id=${id}`),
};

export const operatixService = {
  connectWhatsApp: (proyectoId, code) =>
    axios.post(`${API_BASE_URL}/operatix/connect-whatsapp.php`, { proyecto_id: proyectoId, code }),
  whatsappStatus: (proyectoId) =>
    axios.get(`${API_BASE_URL}/operatix/whatsapp-status.php?proyecto_id=${proyectoId}`),
  disconnectWhatsApp: (proyectoId) =>
    axios.post(`${API_BASE_URL}/operatix/disconnect-whatsapp.php`, { proyecto_id: proyectoId }),
  testConnection: () =>
    axios.get(`${API_BASE_URL}/operatix/test-connection.php`),
};

// ============ WA Training Service (Fase 11.4) ============
export const waTrainingService = {
  // Programas
  getProgramas: (proyectoId) =>
    axios.get(`${API_BASE_URL}/admin/wa-programas.php${proyectoId ? '?proyecto_id=' + proyectoId : ''}`),
  getPrograma: (id) =>
    axios.get(`${API_BASE_URL}/admin/wa-programas.php?id=${id}`),
  createPrograma: (data) =>
    axios.post(`${API_BASE_URL}/admin/wa-programas.php`, data),
  updatePrograma: (id, data) =>
    axios.put(`${API_BASE_URL}/admin/wa-programas.php`, { id, ...data }),
  deletePrograma: (id, hard = false) =>
    axios.delete(`${API_BASE_URL}/admin/wa-programas.php?id=${id}${hard ? '&hard=1' : ''}`),

  // Entregas
  getEntregas: (programaId) =>
    axios.get(`${API_BASE_URL}/admin/wa-entregas.php?programa_id=${programaId}`),
  getEntrega: (id) =>
    axios.get(`${API_BASE_URL}/admin/wa-entregas.php?id=${id}`),
  createEntrega: (data) =>
    axios.post(`${API_BASE_URL}/admin/wa-entregas.php`, data),
  updateEntrega: (id, data) =>
    axios.put(`${API_BASE_URL}/admin/wa-entregas.php`, { id, ...data }),
  reorderEntregas: (programaId, orden) =>
    axios.put(`${API_BASE_URL}/admin/wa-entregas.php`, { action: 'reorder', programa_id: programaId, orden }),
  deleteEntrega: (id) =>
    axios.delete(`${API_BASE_URL}/admin/wa-entregas.php?id=${id}`),

  // Inscripciones
  getInscripciones: (programaId) =>
    axios.get(`${API_BASE_URL}/admin/wa-inscripciones.php?programa_id=${programaId}`),
  getInscripcion: (id) =>
    axios.get(`${API_BASE_URL}/admin/wa-inscripciones.php?id=${id}`),
  createInscripcion: (data) =>
    axios.post(`${API_BASE_URL}/admin/wa-inscripciones.php`, data),
  importInscripciones: (programaId, contactos, fechaInicio) =>
    axios.post(`${API_BASE_URL}/admin/wa-inscripciones.php`, { action: 'import_bulk', programa_id: programaId, contactos, fecha_inicio: fechaInicio }),
  updateInscripcion: (id, data) =>
    axios.put(`${API_BASE_URL}/admin/wa-inscripciones.php`, { id, ...data }),
  deleteInscripcion: (id) =>
    axios.delete(`${API_BASE_URL}/admin/wa-inscripciones.php?id=${id}`),

  // Interacciones (read-only)
  getInteracciones: (params) =>
    axios.get(`${API_BASE_URL}/admin/wa-interacciones.php`, { params }),
};

// ============ Membership Service (Fase 11.8b) ============
export const membershipService = {
  getMisProyectos: () => axios.get(`${API_BASE_URL}/mis-proyectos.php`),
};

// ============ WA Client Dashboard Service (Fase 11.8) ============
export const waClientService = {
  getStatus: (proyectoId) =>
    axios.get(`${API_BASE_URL}/wa-dashboard.php`, { params: { action: 'status', proyecto_id: proyectoId || undefined } }),
  getProgramas: (proyectoId) =>
    axios.get(`${API_BASE_URL}/wa-dashboard.php`, { params: { action: 'programas', proyecto_id: proyectoId || undefined } }),
  getInscripciones: (programaId, proyectoId) =>
    axios.get(`${API_BASE_URL}/wa-dashboard.php`, { params: { action: 'inscripciones', programa_id: programaId, proyecto_id: proyectoId || undefined } }),
  getInteracciones: (params) =>
    axios.get(`${API_BASE_URL}/wa-dashboard.php`, { params: { action: 'interacciones', ...params } }),
  getMetricas: (proyectoId) =>
    axios.get(`${API_BASE_URL}/wa-dashboard.php`, { params: { action: 'metricas', proyecto_id: proyectoId || undefined } }),
  getRanking: (programaId, proyectoId) =>
    axios.get(`${API_BASE_URL}/wa-dashboard.php`, { params: { action: 'ranking', programa_id: programaId, proyecto_id: proyectoId || undefined } }),
  getAnalisisPreguntas: (programaId, proyectoId) =>
    axios.get(`${API_BASE_URL}/wa-dashboard.php`, { params: { action: 'analisis-preguntas', programa_id: programaId, proyecto_id: proyectoId || undefined } }),
  getDetalleEstudiante: (inscripcionId, proyectoId) =>
    axios.get(`${API_BASE_URL}/wa-dashboard.php`, { params: { action: 'detalle-estudiante', inscripcion_id: inscripcionId, proyecto_id: proyectoId || undefined } }),
  connectWhatsApp: (code, proyectoId) =>
    axios.post(`${API_BASE_URL}/wa-dashboard.php`, { action: 'connect-whatsapp', code, proyecto_id: proyectoId || undefined }),
  disconnectWhatsApp: (proyectoId) =>
    axios.post(`${API_BASE_URL}/wa-dashboard.php`, { action: 'disconnect-whatsapp', proyecto_id: proyectoId || undefined }),
};

// ============ Profile Service (Perfil de usuario) ============
export const profileService = {
  getProfile: () => axios.get(`${API_BASE_URL}/profile.php`),
  miSuscripcion: () => axios.get(`${API_BASE_URL}/mi-suscripcion.php`),
  updateProfile: (data) => axios.put(`${API_BASE_URL}/profile.php`, data),
  changePassword: (actual, nueva) =>
    axios.post(`${API_BASE_URL}/profile.php?action=change_password`,
      { password_actual: actual, password_nueva: nueva }),
  uploadAvatar: (file) => {
    const fd = new FormData();
    fd.append('avatar', file);
    return axios.post(`${API_BASE_URL}/profile.php?action=upload_avatar`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } });
  }
};

// ============ Mi Ranking Service (Ranking personal) ============
export const miRankingService = {
  getRanking: (documentoId) => axios.get(`${API_BASE_URL}/mi-ranking.php`, { params: { documento_id: documentoId } }),
  getRankingGlobal: () => axios.get(`${API_BASE_URL}/mi-ranking.php`)
};

// ============ Media Library Service (Fase 15) ============
export const mediaLibraryService = {
  // Media CRUD
  list: (params = {}) =>
    axios.get(`${API_BASE_URL}/admin/media-library.php`, { params }),
  getById: (id) =>
    axios.get(`${API_BASE_URL}/admin/media-library.php?id=${id}`),
  upload: (formData) =>
    axios.post(`${API_BASE_URL}/admin/media-library.php`, formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }),
  importVimeo: (data) =>
    axios.post(`${API_BASE_URL}/admin/media-library.php?action=import_vimeo`, data),
  update: (id, data) =>
    axios.put(`${API_BASE_URL}/admin/media-library.php`, { id, ...data }),
  delete: (id) =>
    axios.delete(`${API_BASE_URL}/admin/media-library.php?id=${id}`),
  importVimeoTranscript: (mediaId, idioma = 'es') =>
    axios.post(`${API_BASE_URL}/admin/media-library.php?action=import_vimeo_transcript`,
      { media_id: mediaId, idioma }),
  regenerateSummary: (mediaId) =>
    axios.post(`${API_BASE_URL}/admin/media-library.php?action=regenerate_summary`,
      { media_id: mediaId }),
  // Reimporta de Vimeo duración + transcripción de un video ya existente y
  // resincroniza el progreso automáticamente.
  reimportVimeo: (mediaId, idioma = 'es') =>
    axios.post(`${API_BASE_URL}/admin/media-library.php?action=reimport_vimeo`,
      { media_id: mediaId, idioma }),
  // Resincroniza el progreso sin reimportar (media_id → ese video; curso_id → todo el curso)
  resyncProgreso: (payload) =>
    axios.post(`${API_BASE_URL}/admin/media-library.php?action=resync_progreso`, payload),
};

// Páginas personalizables por proyecto (admin CRUD)
export const proyectoPaginaService = {
  list: (proyectoId) =>
    axios.get(`${API_BASE_URL}/admin/proyecto-paginas.php?proyecto_id=${proyectoId}`),
  get: (id) =>
    axios.get(`${API_BASE_URL}/admin/proyecto-paginas.php?id=${id}`),
  create: (data) =>
    axios.post(`${API_BASE_URL}/admin/proyecto-paginas.php`, data),
  update: (data) =>
    axios.put(`${API_BASE_URL}/admin/proyecto-paginas.php`, data),
  duplicate: (data) =>
    axios.post(`${API_BASE_URL}/admin/proyecto-paginas.php?action=duplicate`, data),
  remove: (id) =>
    axios.delete(`${API_BASE_URL}/admin/proyecto-paginas.php?id=${id}`),
};

// Páginas visibles para el usuario actual (runtime, filtradas por país/rol/idioma)
export const misPaginasService = {
  // Menú: lista liviana (sin secciones) de páginas visibles
  menu: (proyectoId, idioma) =>
    axios.get(`${API_BASE_URL}/mis-paginas.php`, { params: { proyecto_id: proyectoId, idioma, only_menu: 1 } }),
  // Una página con sus secciones
  getBySlug: (proyectoId, slug, idioma) =>
    axios.get(`${API_BASE_URL}/mis-paginas.php`, { params: { proyecto_id: proyectoId, slug, idioma } }),
  // Página de contenidos personalizada del usuario (si existe) — reemplaza /contenidos
  contenidos: (proyectoId, idioma) =>
    axios.get(`${API_BASE_URL}/mis-paginas.php`, { params: { proyecto_id: proyectoId, idioma, proposito: 'contenidos' } }),
};

// Fase H2c — Evaluación por Realtime API
export const evaluacionRealtimeService = {
  iniciar: (documentId) =>
    axios.post(`${API_BASE_URL}/evaluacion-realtime/iniciar.php`, { document_id: documentId }),
  guardar: (payload) =>
    axios.post(`${API_BASE_URL}/evaluacion-realtime/guardar.php`, payload),
};

// Exámenes de un curso asociados al curso, a un módulo o a una lección (admin).
export const mentorExamenesService = {
  list: (documentoId) => axios.get(`${API_BASE_URL}/admin/mentor-examenes.php`, { params: { documento_id: documentoId } }),
  crear: (data) => axios.post(`${API_BASE_URL}/admin/mentor-examenes.php`, data),
  editar: (data) => axios.put(`${API_BASE_URL}/admin/mentor-examenes.php`, data),
  borrar: (id) => axios.delete(`${API_BASE_URL}/admin/mentor-examenes.php`, { params: { id } }),
};

// Evaluación escrita (sin micrófono): preguntas server-side + calificación por respuesta.
// El guardado del resultado reutiliza evaluacion-realtime/guardar.php.
export const evaluacionEscritaService = {
  preguntas: (documentId, proyectoId, examenId) =>
    axios.post(`${API_BASE_URL}/evaluacion-escrita/preguntas.php`, { document_id: documentId, proyecto_id: proyectoId || null, examen_id: examenId || null }),
  // Datos para la pantalla de entrada (título, preguntas, nota mínima, intentos): no crea intento.
  info: (documentId, examenId) =>
    axios.post(`${API_BASE_URL}/evaluacion-escrita/preguntas.php`, { document_id: documentId, examen_id: examenId || null, info: 1 }),
  calificar: (intentoId, numero, respuesta) =>
    axios.post(`${API_BASE_URL}/evaluacion-escrita/calificar.php`, { intento_id: intentoId, numero, respuesta }),
  guardar: (payload) =>
    axios.post(`${API_BASE_URL}/evaluacion-realtime/guardar.php`, payload),
};

// Fase H2a/b — Preguntas sugeridas
export const preguntasSugeridasService = {
  list: (documentoId) =>
    axios.get(`${API_BASE_URL}/admin/preguntas-sugeridas.php`, { params: { documento_id: documentoId } }),
  create: (data) =>
    axios.post(`${API_BASE_URL}/admin/preguntas-sugeridas.php`, data),
  update: (data) =>
    axios.put(`${API_BASE_URL}/admin/preguntas-sugeridas.php`, data),
  remove: (id) =>
    axios.delete(`${API_BASE_URL}/admin/preguntas-sugeridas.php`, { params: { id } }),
  bulkImport: (documentoId, filas) =>
    axios.post(`${API_BASE_URL}/admin/preguntas-sugeridas.php?action=bulk_import`,
      { documento_id: documentoId, filas }),
};

export const mediaSubtituloService = {
  list: (mediaId) =>
    axios.get(`${API_BASE_URL}/admin/media-subtitulos.php?media_id=${mediaId}`),
  get: (mediaId, idioma) =>
    axios.get(`${API_BASE_URL}/admin/media-subtitulos.php?media_id=${mediaId}&idioma=${idioma}`),
  save: ({ media_id, idioma, formato, contenido, origen }) =>
    axios.post(`${API_BASE_URL}/admin/media-subtitulos.php`,
      { media_id, idioma, formato, contenido, origen }),
  remove: (mediaId, idioma) =>
    axios.delete(`${API_BASE_URL}/admin/media-subtitulos.php?media_id=${mediaId}&idioma=${idioma}`),
};

export const mediaTagService = {
  list: (proyectoId) =>
    axios.get(`${API_BASE_URL}/admin/media-tags.php${proyectoId ? '?proyecto_id=' + proyectoId : ''}`),
  create: (data) =>
    axios.post(`${API_BASE_URL}/admin/media-tags.php`, data),
  update: (id, data) =>
    axios.put(`${API_BASE_URL}/admin/media-tags.php`, { id, ...data }),
  delete: (id) =>
    axios.delete(`${API_BASE_URL}/admin/media-tags.php?id=${id}`),
};

// ============ Documento Media Service (Fase 15.8) ============
export const documentoMediaService = {
  list: (documentoId) =>
    axios.get(`${API_BASE_URL}/admin/documento-media.php?documento_id=${documentoId}`),
  link: (documentoId, mediaId) =>
    axios.post(`${API_BASE_URL}/admin/documento-media.php`, { documento_id: documentoId, media_id: mediaId }),
  unlink: (id) =>
    axios.delete(`${API_BASE_URL}/admin/documento-media.php?id=${id}`),
};

// ============ Entregas de archivos en las lecciones ============
// El alumno sube su entrega (mentor/entregas.php); el tutor o el admin la revisan
// (mentor/entregas-revision.php). Los archivos se descargan como blob con el token.
export const entregasService = {
  mia: (leccionId) =>
    axios.get(`${API_BASE_URL}/mentor/entregas.php?leccion_id=${leccionId}`),
  enviar: (leccionId, archivos, comentario, onUploadProgress, reemplazar = false) => {
    const fd = new FormData();
    fd.append('leccion_id', leccionId);
    if (reemplazar) fd.append('reemplazar', '1');
    fd.append('comentario', comentario || '');
    archivos.forEach(f => fd.append('archivos[]', f));
    return axios.post(`${API_BASE_URL}/mentor/entregas.php`, fd, { onUploadProgress });
  },
  descargar: (id, i, tutor = false) =>
    axios.get(`${API_BASE_URL}/mentor/entregas.php?descargar=${id}&i=${i}${tutor ? '&tutor=1' : ''}`, { responseType: 'blob' }),
  resumen: () =>
    axios.get(`${API_BASE_URL}/mentor/entregas-revision.php?accion=resumen`),
  lista: (filtros = {}) =>
    axios.get(`${API_BASE_URL}/mentor/entregas-revision.php`, { params: { accion: 'lista', ...filtros } }),
  tutores: (proyectoId) =>
    axios.get(`${API_BASE_URL}/mentor/entregas-revision.php?accion=tutores&proyecto_id=${proyectoId}`),
  mias: (proyectoId) =>
    axios.get(`${API_BASE_URL}/mentor/entregas.php`, { params: { mias: 1, ...(proyectoId ? { proyecto_id: proyectoId } : {}) } }),
  grupos: (proyectoId) =>
    axios.get(`${API_BASE_URL}/mentor/entregas-revision.php?accion=grupos&proyecto_id=${proyectoId}`),
  guardarGrupo: (data) =>
    axios.post(`${API_BASE_URL}/mentor/entregas-revision.php`, { accion: 'guardar_grupo', ...data }),
  lecciones: (proyectoId) =>
    axios.get(`${API_BASE_URL}/mentor/entregas-revision.php?accion=lecciones&proyecto_id=${proyectoId}`),
  reabrir: (id) =>
    axios.post(`${API_BASE_URL}/mentor/entregas-revision.php`, { accion: 'reabrir', id }),
  tomar: (id, soltar = false) =>
    axios.post(`${API_BASE_URL}/mentor/entregas-revision.php`, { accion: soltar ? 'soltar' : 'tomar', id }),
  calificar: (id, { decision, nota, comentario, archivos = [], rubrica = null }) => {
    const fd = new FormData();
    if (rubrica) fd.append('rubrica', JSON.stringify(rubrica));
    fd.append('accion', 'calificar');
    fd.append('id', id);
    fd.append('decision', decision);
    fd.append('nota', nota ?? '');
    fd.append('comentario', comentario || '');
    archivos.forEach(f => fd.append('archivos[]', f));
    return axios.post(`${API_BASE_URL}/mentor/entregas-revision.php`, fd);
  },
};

// Guarda en el equipo un archivo de una entrega (descarga autenticada).
export const descargarEntrega = async (id, i, nombre, tutor = false) => {
  const res = await entregasService.descargar(id, i, tutor);
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url; a.download = nombre || 'archivo';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

// ============ Course Builder Service (Fase 15.4) ============
export const mentorCursoService = {
  // Cursos
  getEstructura: (documentoId) =>
    axios.get(`${API_BASE_URL}/admin/mentor-cursos.php?documento_id=${documentoId}`),
  getCurso: (id) =>
    axios.get(`${API_BASE_URL}/admin/mentor-cursos.php?id=${id}`),
  listCursos: (proyectoId) =>
    axios.get(`${API_BASE_URL}/admin/mentor-cursos.php${proyectoId ? '?proyecto_id=' + proyectoId : ''}`),
  listCursosConEstructura: (proyectoId) =>
    axios.get(`${API_BASE_URL}/admin/mentor-cursos.php?con_estructura=1${proyectoId ? '&proyecto_id=' + proyectoId : ''}`),
  createCurso: (data) =>
    axios.post(`${API_BASE_URL}/admin/mentor-cursos.php`, data),
  updateCurso: (id, data) =>
    axios.put(`${API_BASE_URL}/admin/mentor-cursos.php`, { id, ...data }),
  deleteCurso: (id) =>
    axios.delete(`${API_BASE_URL}/admin/mentor-cursos.php?id=${id}`),

  // Módulos
  createModulo: (data) =>
    axios.post(`${API_BASE_URL}/admin/mentor-modulos.php`, data),
  updateModulo: (id, data) =>
    axios.put(`${API_BASE_URL}/admin/mentor-modulos.php`, { id, ...data }),
  deleteModulo: (id) =>
    axios.delete(`${API_BASE_URL}/admin/mentor-modulos.php?id=${id}`),

  // Lecciones
  createLeccion: (data) =>
    axios.post(`${API_BASE_URL}/admin/mentor-lecciones.php`, data),
  updateLeccion: (id, data) =>
    axios.put(`${API_BASE_URL}/admin/mentor-lecciones.php`, { id, ...data }),
  deleteLeccion: (id) =>
    axios.delete(`${API_BASE_URL}/admin/mentor-lecciones.php?id=${id}`),

  // Items (contenido en lecciones)
  getItems: (leccionId) =>
    axios.get(`${API_BASE_URL}/admin/mentor-leccion-items.php?leccion_id=${leccionId}`),
  addItem: (data) =>
    axios.post(`${API_BASE_URL}/admin/mentor-leccion-items.php`, data),
  updateItem: (id, data) =>
    axios.put(`${API_BASE_URL}/admin/mentor-leccion-items.php`, { id, ...data }),
  removeItem: (id) =>
    axios.delete(`${API_BASE_URL}/admin/mentor-leccion-items.php?id=${id}`),

  // Reordenar (drag-and-drop)
  reorder: (tipo, orden, reparent) =>
    axios.put(`${API_BASE_URL}/admin/mentor-reorder.php`, { tipo, orden, reparent }),
};

// ============ Docentes por proyecto ============
export const mentorDocenteService = {
  list: (proyectoId, opts = {}) => {
    const p = new URLSearchParams({ proyecto_id: proyectoId });
    if (opts.q) p.set('q', opts.q);
    return axios.get(`${API_BASE_URL}/admin/mentor-docentes.php?${p.toString()}`);
  },
  // Catálogo global de docentes activos (para resolver nombres sin recorrer proyectos)
  listTodos: () => axios.get(`${API_BASE_URL}/admin/mentor-docentes.php?todos=1`),
  get: (id) => axios.get(`${API_BASE_URL}/admin/mentor-docentes.php?id=${id}`),
  create: (data) => axios.post(`${API_BASE_URL}/admin/mentor-docentes.php`, data),
  update: (id, data) => axios.put(`${API_BASE_URL}/admin/mentor-docentes.php`, { id, ...data }),
  remove: (id) => axios.delete(`${API_BASE_URL}/admin/mentor-docentes.php?id=${id}`),
};

export default {
  auth: authService,
  courses: courseService,
  lessons: lessonService,
  voice: voiceService,
  evaluations: evaluationService,
  questions: questionService,
  progress: progressService,
  admin: adminService,
  users: userService,
  certificates: certificadoService,
  completedCourses: completedCourseService,
  consulta: consultaService,
  attachments: attachmentService,
  transcriptions: transcriptionService,
  reto: retoService,  // Servicio de reto semanal
  notificaciones: notificacionService,
  mentor2: mentor2Service,
  seguimiento: seguimientoService,
  proyectos: proyectoService,
  convenios: convenioService,
  especialidades: especialidadService,
  paises: paisService
};
